// HTML5 Audio alert service for active browser tab notifications
// Uses configurable audio file (e.g. /sounds/notification.mp3) instead of synthesized Web Audio

export const NOTIFICATION_SOUND_PATH = '/sounds/notification.mp3';

const MUTE_STORAGE_KEY = 'ayur_audio_muted';

let audioElement = null;
let configuredSoundPath = NOTIFICATION_SOUND_PATH;
let isUnlocked = false;

/**
 * Returns a reusable HTML5 Audio instance for notification chime playback.
 * Lazily instantiates the Audio element with preload='auto'.
 */
export function getAudioInstance() {
  if (typeof window === 'undefined' || typeof window.Audio === 'undefined') {
    return null;
  }

  if (!audioElement) {
    try {
      audioElement = new window.Audio(configuredSoundPath);
      audioElement.preload = 'auto';
    } catch (err) {
      console.warn('Failed to initialize Audio instance for notification alert:', err);
      audioElement = null;
    }
  }

  return audioElement;
}

/**
 * Resets audio state and instances (primarily used for unit testing).
 */
export function _resetAudioForTesting() {
  audioElement = null;
  configuredSoundPath = NOTIFICATION_SOUND_PATH;
  isUnlocked = false;
}

/**
 * Allows overriding the sound path dynamically in tests.
 */
export function _setSoundPathForTesting(newPath) {
  configuredSoundPath = newPath;
  audioElement = null;
}

/**
 * Initializes audio unlock on first user gesture (click, keydown, pointerdown).
 * Browsers enforce strict autoplay policies that block unprompted audio until user gesture.
 */
export function initAudioUnlock() {
  if (typeof window === 'undefined' || isUnlocked) return;

  const unlock = () => {
    isUnlocked = true;
    try {
      const audio = getAudioInstance();
      if (audio && typeof audio.load === 'function') {
        audio.load();
      }
    } catch (e) {
      console.debug('Audio unlock deferred or failed:', e);
    } finally {
      window.removeEventListener('click', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      window.removeEventListener('pointerdown', unlock, true);
    }
  };

  window.addEventListener('click', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('pointerdown', unlock, true);
}

/**
 * Checks whether audio notifications are muted by user preference.
 */
export function isAudioMuted() {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Persists audio notification mute preference to localStorage.
 */
export function setAudioMuted(muted) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? 'true' : 'false');
  } catch (e) {
    console.warn('Failed to persist audio mute setting', e);
  }
}

/**
 * Toggles audio mute preference and returns the new state.
 */
export function toggleAudioMute() {
  const next = !isAudioMuted();
  setAudioMuted(next);
  return next;
}

/**
 * Plays the notification sound using the configured audio file.
 * Resets playback position to beginning (currentTime = 0) so repeated alerts play cleanly.
 * Returns true if playback was successfully triggered, false if muted or unavailable.
 */
export function playNotificationSound() {
  if (isAudioMuted()) {
    return false;
  }

  try {
    const audio = getAudioInstance();
    if (!audio) {
      return false;
    }

    // Reset playback position so rapid or queued notifications play immediately from start
    try {
      audio.currentTime = 0;
    } catch (seekErr) {
      // Non-fatal if audio metadata is not yet seekable
      console.debug('Failed to reset audio currentTime:', seekErr);
    }

    const playPromise = audio.play();

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        // Safe handling of autoplay rejection, missing file, or user interaction restrictions
        console.warn('Notification audio playback prevented or failed:', err);
      });
    }

    return true;
  } catch (err) {
    // Fail-safe protection: never let audio errors crash the app or interrupt notification handling
    console.warn('Notification audio error:', err);
    return false;
  }
}
